/**
 * External Imports
 */
import { FC, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuButton, NeuRadio } from '@neutron/react';

/**
 * Internal Imports
 */
import { RootState } from '../../../redux/store';
import { useGetUserRolesQuery } from '../../../redux/apis/Admin.api';
import { useGetDivisionsQuery } from '../../../redux/apis/Config.api';
import { usePostUserAccessMutation } from '../../../redux/apis/Users.api';
import MultiSelectDropdown from '../../multiSelect/MultiSelectDropdown';
import Loading from '../../shared/Loading';
import { handleModal } from '../../../services/ModalPortal/utils';

/**
 * Global Type Definition Imports
 */
import { IFacility } from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CardHeader,
  InputHeading,
  ModalCardContent,
  ModalFooter,
  ModalTextArea,
  ModalTitle,
  ProvisioningCard
} from './AddProvisioningModal.styles';

interface ProvisioningModalProps {
  facilities: IFacility[];
}
interface UserRoles {
  id: string;
  value: string;
}
const AddProvisioningModal: FC<ProvisioningModalProps> = ({
  facilities = []
}) => {
  /** State variables */
  const [showAccessLevel, setShowAccessLevel] = useState<boolean>(true);
  const [accessLevelList, setAccessLevelList] = useState<any[]>([]);
  const [defaultSelection, setDefaultSelection] = useState([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedAccessLevel, setSelectedAccessLevel] =
    useState<string>('facility');
  const [defaultUserRoles, setDefaultUserRoles] = useState([]);
  const [selectedUserRole, setSelectedUserRole] = useState<string[]>([]);
  const [selected34Ids, setSelected34Ids] = useState<string>('');
  const [selected34IdArray, setSelected34IdArray] = useState<string[]>([]);
  const [isInvalid34IDs, setIsInvalid34IDs] = useState<boolean>(false);
  const [showButton, setShowButton] = useState<boolean>(false);
  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  const {
    data: divisionsData,
    // isError: isGetDivisionsError,
    // isFetching: isGetDivisionsFetching,
    isLoading: isGetDivisionsLoading
  } = useGetDivisionsQuery(undefined, {
    refetchOnMountOrArgChange: 120
  });
  const {
    data: userRolesData,
    // isError: isUserRolesError,
    // isFetching: isUserRolesFetching,
    isLoading: isUserRolesLoading
  } = useGetUserRolesQuery(undefined, {
    refetchOnMountOrArgChange: 60
  });
  const [
    postUserAccess
    // {
    //   isError: isUserAccessError,
    //   isLoading: isUserAccessLoading,
    //   isSuccess: isUserAccessSuccess
    // }
  ] = usePostUserAccessMutation();

  /** CTA block */
  const handleSelected = (selected: []) => {
    const selectedIdArray =
      selected &&
      selected.length > 0 &&
      // TODO: Scope a type for division and split the logic between facility and division
      selected.map((item: IFacility) =>
        selectedAccessLevel === 'division' ? item.value : item.facilityId
      );
    if (selectedIdArray) setSelectedItems(selectedIdArray);
  };

  const handleSelectedUserRoles = (selected: []) => {
    const selectedRoles =
      selected &&
      selected.length > 0 &&
      selected.map((item: UserRoles) => item.id);
    if (selectedRoles) setSelectedUserRole(selectedRoles);
  };

  const onAccessLevelChange = (selectedLevel: string) => {
    setShowAccessLevel(true);
    if (selectedLevel === 'division') {
      setAccessLevelList(divisionsData ?? []);
    } else if (selectedLevel === 'facility') {
      setAccessLevelList(facilities);
      // appInsights.trackEvent({
      //   name: 'Facility_Selected'
      // });
    } else {
      setShowAccessLevel(false);
      // appInsights.trackEvent({
      //   name: 'Facility_Selection_Failed'
      // });
    }
    // Reset selected items when facility/division change
    if (selectedItems && selectedItems.length > 0) {
      setDefaultSelection([]);
    }
    setSelectedAccessLevel(selectedLevel);
  };

  const handle34IdChange = (event: any) => {
    const target = event.target as HTMLInputElement;
    const selected34IDs = target.value;
    setSelected34Ids(selected34IDs);
    if (selected34IDs) {
      // Split the input string with space, comma or new line
      const formated34IDs = selected34IDs.trim().split(/,|\s+|\n+}/);
      const formatted34IDarray = formated34IDs.filter((item: string) => item);
      setSelected34IdArray(formatted34IDarray);
      const condition = /^([a-z]{3})?([0-9]{4})+$/i;
      const invalidIDs = formatted34IDarray.filter(
        (item: string) => !condition.test(item)
      );
      setIsInvalid34IDs(false);
      // appInsights.trackEvent({
      //   name: 'User_Configuration_Change'
      // });
      if (invalidIDs && invalidIDs.length > 0) {
        setIsInvalid34IDs(true);
        // appInsights.trackEvent({
        //   name: 'User_Configuration_Change_Failed'
        // });
      }
    }
  };

  const handleReset = () => {
    setDefaultUserRoles([]);
    setSelectedAccessLevel('facility');
    setDefaultSelection([]);
    setSelectedUserRole([]);
    setSelected34Ids('');
    setSelected34IdArray([]);
  };

  const handleSubmit = () => {
    if (showButton) {
      const postParams = {
        employeeId: '',
        access: {
          accessLevel: selectedAccessLevel,
          accessLevelIds:
            selectedAccessLevel === 'master' ? ['HCA'] : selectedItems
        },
        roles: selectedUserRole,
        primaryUnit: [
          {
            facilityId: '',
            unitId: ''
          }
        ]
      };
      selected34IdArray.forEach(async (item: string) => {
        postParams.employeeId = item;
        await postUserAccess(postParams);
      });
      setShowButton(false);
      handleReset();
      handleModal(modalContentRef);
    }
  };
  /** CTA block - End */

  const showSubmitButton = () => {
    if (
      !isInvalid34IDs &&
      selectedUserRole.length > 0 &&
      selectedAccessLevel &&
      selected34Ids
    ) {
      if (selectedAccessLevel === 'master') {
        return true;
      }
      if (selectedAccessLevel !== 'master' && selectedItems.length > 0) {
        return true;
      }
      return false;
    }
    return false;
  };

  useEffect(() => {
    setAccessLevelList(facilities);
  }, [facilities]);

  useEffect(() => {
    if (showSubmitButton()) {
      setShowButton(true);
    } else {
      setShowButton(false);
    }
  }, [
    isInvalid34IDs,
    selectedUserRole,
    selectedAccessLevel,
    selectedItems,
    selected34Ids
  ]);

  return (
    <ProvisioningCard
      id="Modal-Provision"
      className="show-modal"
      unit="card"
      kind="elevated"
      ref={modalContentRef}
    >
      <CardHeader className="neu-bg-plain-0" unit="header">
        <ModalTitle>Provisioning</ModalTitle>
        <NeuButton
          id="Modal-Provision-Close-Button"
          kind="tertiary"
          icon="close"
          size="small"
          onClick={() => handleModal(modalContentRef)}
        >
          Close
        </NeuButton>
      </CardHeader>
      {isUserRolesLoading || isGetDivisionsLoading ? (
        <ModalCardContent unit="content">
          <Loading label="data" />
        </ModalCardContent>
      ) : (
        <>
          <ModalCardContent unit="content">
            <InputHeading>User Role</InputHeading>
            {userRolesData && (
              // TODO: Convert to Dropdown component
              <MultiSelectDropdown
                options={userRolesData}
                defaultselection={defaultUserRoles}
                selected={handleSelectedUserRoles}
                id="Modal-Provision-Select-User-Role"
                showArrow={false}
                searchable={false}
                chipsInside={false}
              />
            )}

            <NeuRadio
              data-testid="provision-modal-access-level-radio-group"
              unit="group"
              label="Access Level"
              name="accessLevel"
              required
              value={selectedAccessLevel}
              onNeuChange={e => onAccessLevelChange(e.detail.value)}
            >
              <NeuRadio
                data-testid="provision-modal-access-level-radio-item-facility"
                className="mt-1 me-4"
                unit="item"
                label="Facility"
                name="accessLevel"
                value="facility"
              />
              <NeuRadio
                data-testid="provision-modal-access-level-radio-item-division"
                className="me-4"
                unit="item"
                label="Division"
                name="accessLevel"
                value="division"
              />
              <NeuRadio
                data-testid="provision-modal-access-level-radio-item-master"
                className="me-4"
                unit="item"
                label="Corporate"
                name="accessLevel"
                value="master"
              />
            </NeuRadio>

            <div style={{ minHeight: '6em' }}>
              {showAccessLevel && accessLevelList && (
                <>
                  <InputHeading>
                    {selectedAccessLevel.charAt(0).toUpperCase() +
                      selectedAccessLevel.slice(1)}
                  </InputHeading>
                  {/* TODO: Convert to Dropdown component */}
                  <MultiSelectDropdown
                    options={accessLevelList}
                    defaultselection={defaultSelection}
                    selected={handleSelected}
                    id="Facility-Division-Select"
                    showArrow={false}
                    searchable={false}
                    chipsInside={false}
                  />
                </>
              )}
            </div>

            <ModalTextArea
              label="Users to Provision"
              required
              tip="Enter 3/4 IDs separated by comma, space or new line"
              value={selected34Ids}
              onNeuChange={handle34IdChange}
            />
          </ModalCardContent>
          <ModalFooter unit="footer">
            <NeuButton
              color="red"
              size="small"
              onClick={(e: any) => {
                onAccessLevelChange(e);
                handleReset();
              }}
            >
              Reset
            </NeuButton>
            <NeuButton
              color="blue"
              disabled={!showButton}
              size="small"
              style={{ float: 'right' }}
              onClick={handleSubmit}
            >
              Submit
            </NeuButton>
          </ModalFooter>
        </>
      )}
    </ProvisioningCard>
  );
};
const mapReduxStateToProps = (state: RootState) => {
  return {
    facilities: state.ConfigReducer.userAccessibleFacilities,
    modalState: state.ModalReducer.modal
  };
};

export default connect(mapReduxStateToProps)(AddProvisioningModal);
